import React, { useEffect, useMemo, useState, useCallback } from "react";
import Header from "./Header";
import { Link } from "react-router-dom";
import { api } from "../server";

const STATUSES = [
  "",
  "Ordered",
  "Accepted",
  "Picked",
  "Delivered",
  "Cancelled",
];

const money = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(Number(n)) ? Number(n) : 0);

const statusColor = (s) =>
  ({
    Ordered: "bg-secondary-subtle text-secondary",
    Accepted: "bg-info-subtle text-info",
    Picked: "bg-warning-subtle text-warning",
    Delivered: "bg-success-subtle text-success",
    Cancelled: "bg-danger-subtle text-danger",
  })[s] || "bg-light text-dark";

function Orders() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [orderType, setOrderType] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (orderType) params.set("orderType", orderType);
      if (from) params.set("from", from);
      if (to) params.set("to", to);
      params.set("limit", "200");
      const data = await api.get(`/api/admin/orders?${params.toString()}`);
      setRows(data?.orders || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [status, orderType, from, to]);
  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return rows;
    return rows.filter(
      (r) =>
        String(r.orderNumber || "")
          .toLowerCase()
          .includes(query) ||
        String(r.customer?.name || "")
          .toLowerCase()
          .includes(query) ||
        String(r.customer?.phone || "")
          .toLowerCase()
          .includes(query) ||
        String(r.driver?.name || "")
          .toLowerCase()
          .includes(query),
    );
  }, [rows, q]);

  const stats = useMemo(() => {
    const total = rows.length;
    const delivered = rows.filter((r) => r.status === "Delivered").length;
    const inProgress = rows.filter((r) =>
      ["Ordered", "Accepted", "Picked"].includes(r.status),
    ).length;
    const revenue = rows
      .filter((r) => r.status === "Delivered")
      .reduce((s, r) => s + (Number(r.totalAmount) || 0), 0);
    return { total, delivered, inProgress, revenue };
  }, [rows]);

  const resetFilters = () => {
    setQ("");
    setStatus("");
    setOrderType("");
    setFrom("");
    setTo("");
  };

  return (
    <>
      <Header />
      <div id="db-wrapper">
        <main id="page-content">
          <section className="container-fluid p-4 pt-3">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
              <div>
                <h3 className="mb-0 lh-1 ls-sm fw-normal">Orders</h3>
                <div className="text-secondary lh-1">
                  All customer orders • Status • Driver assignments
                </div>
              </div>
              <div className="d-flex gap-1">
                <button
                  type="button"
                  className="btn btn-outline-secondary border-light px-4 text-dark bg-white"
                  onClick={resetFilters}
                >
                  <i className="bi bi-funnel me-2" />
                  Reset Filters
                </button>
                <button
                  type="button"
                  className="btn btn-light border px-4"
                  onClick={fetchRows}
                >
                  <i className="bi bi-arrow-clockwise me-2" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="row g-2 mb-2">
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-cart" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Showing</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.total}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-success-subtle text-success"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-check2-circle" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Delivered</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.delivered}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-warning-subtle text-warning"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-truck" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">
                        In Progress
                      </div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {stats.inProgress}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-3">
                <div className="card border-0 shadow-sm rounded-4 h-100">
                  <div className="card-body p-3 d-flex align-items-center gap-2">
                    <div
                      className="rounded-3 d-inline-flex align-items-center justify-content-center bg-info-subtle text-info"
                      style={{ width: 40, height: 40 }}
                    >
                      <i className="bi bi-cash-stack" />
                    </div>
                    <div>
                      <div className="text-secondary small lh-1">Revenue</div>
                      <div className="fs-5 fw-semibold lh-1 mt-1">
                        {money(stats.revenue)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4">
              <div className="card-body p-3 p-md-4">
                <div className="d-flex align-items-center gap-2 mb-3">
                  <div
                    className="rounded-3 d-inline-flex align-items-center justify-content-center bg-primary-subtle text-primary"
                    style={{ width: 40, height: 40 }}
                  >
                    <i className="bi bi-search" />
                  </div>
                  <div>
                    <div className="fw-semibold lh-1">Search & Filters</div>
                    <div className="text-secondary small">
                      By order #, customer, driver, status, type, date range
                    </div>
                  </div>
                </div>
                <div className="row g-2">
                  <div className="col-12 col-md-4">
                    <label className="form-label mb-1 fw-normal">Search</label>
                    <input
                      className="form-control"
                      placeholder="Order # / customer / driver"
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label mb-1 fw-normal">Status</label>
                    <select
                      className="form-select"
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option value="">All</option>
                      {STATUSES.filter(Boolean).map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label mb-1 fw-normal">Type</label>
                    <select
                      className="form-select"
                      value={orderType}
                      onChange={(e) => setOrderType(e.target.value)}
                    >
                      <option value="">All</option>
                      <option value="individual">Individual</option>
                      <option value="corporate">Corporate</option>
                    </select>
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label mb-1 fw-normal">From</label>
                    <input
                      type="date"
                      className="form-control"
                      value={from}
                      onChange={(e) => setFrom(e.target.value)}
                    />
                  </div>
                  <div className="col-6 col-md-2">
                    <label className="form-label mb-1 fw-normal">To</label>
                    <input
                      type="date"
                      className="form-control"
                      value={to}
                      onChange={(e) => setTo(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="card border-0 shadow-sm rounded-4 mt-2">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-5 text-secondary">
                    <i className="bi bi-inbox fs-1 d-block mb-2 opacity-50" />
                    No orders found
                  </div>
                ) : (
                  <div className="table-responsive border-0 overflow-y-hidden bg-white rounded-4">
                    <table className="table mb-0 text-nowrap table-centered">
                      <tbody>
                        {filtered.map((r, index) => (
                          <tr key={r._id}>
                            <td>{index + 1}.</td>
                            <td className="lh-sm py-4 text-wrap">
                              <span className="text-dark">Order #</span> &mdash;{" "}
                              {r.orderNumber}
                              <br />
                              <span className="text-dark">Customer</span>{" "}
                              &mdash; {r.customer?.name || "—"}
                              <br />
                              <span className="text-dark">
                                Phone
                              </span> &mdash; {r.customer?.phone || "—"}
                              <br />
                              <span className="text-dark">
                                Type
                              </span> &mdash; {r.orderType || "individual"}
                              <br />
                              <span className="text-dark">
                                Items
                              </span> &mdash;{" "}
                              {(r.items || [])
                                .map((i) => `${i.productName} × ${i.quantity}`)
                                .join(", ") || "—"}
                              <br />
                              <span className="text-dark">
                                Amount
                              </span> &mdash; {money(r.totalAmount)}
                              <br />
                              <span className="text-dark">
                                Driver
                              </span> &mdash;{" "}
                              {r.driver?.name || (
                                <span className="text-warning">Unassigned</span>
                              )}
                              <br />
                              <span className="text-dark">
                                Status
                              </span> &mdash;{" "}
                              <span
                                className={`badge ${statusColor(r.status)}`}
                              >
                                {r.status}
                              </span>
                              <br />
                              <span className="text-dark">
                                Date
                              </span> &mdash;{" "}
                              <span className="text-secondary">
                                {r.formattedDate}
                              </span>
                              {/* ── Payment info (corporate advance orders) ── */}
                              {r.orderType === "corporate" &&
                                r.paymentMode === "advance" &&
                                (() => {
                                  const paid = Number(r.amountPaid || 0);
                                  const due = Math.max(
                                    0,
                                    Number(r.totalAmount || 0) - paid,
                                  );
                                  const pct =
                                    r.totalAmount > 0
                                      ? Math.min(
                                          100,
                                          Math.round(
                                            (paid / r.totalAmount) * 100,
                                          ),
                                        )
                                      : 0;
                                  const fullPaid = due <= 0;
                                  return (
                                    <>
                                      <br />
                                      <span className="text-dark">
                                        Payment
                                      </span>{" "}
                                      &mdash; Advance&nbsp;
                                      <span className="badge bg-info-subtle text-info">
                                        {money(paid)} paid
                                      </span>
                                      &nbsp;
                                      {fullPaid ? (
                                        <span className="badge bg-success-subtle text-success">
                                          Fully paid
                                        </span>
                                      ) : (
                                        <span className="badge bg-danger-subtle text-danger">
                                          {money(due)} due
                                        </span>
                                      )}
                                      <div
                                        className="progress mt-1 mb-0"
                                        style={{ height: 5, width: 160 }}
                                      >
                                        <div
                                          className={`progress-bar ${fullPaid ? "bg-success" : "bg-primary"}`}
                                          style={{ width: `${pct}%` }}
                                        />
                                      </div>
                                      <small className="text-secondary">
                                        {pct}% paid
                                      </small>
                                    </>
                                  );
                                })()}
                              {/* credit orders — just label, no progress */}
                              {r.orderType === "corporate" &&
                                r.paymentMode === "credit" && (
                                  <>
                                    <br />
                                    <span className="text-dark">
                                      Payment
                                    </span>{" "}
                                    &mdash;{" "}
                                    <span className="badge bg-warning-subtle text-warning">
                                      On credit
                                    </span>
                                  </>
                                )}
                            </td>
                            <td>
                              <div className="d-flex flex-column gap-1 align-items-start">
                                <Link
                                  to={`/orders/${r._id}`}
                                  className="badge bg-primary-soft cursor-pointer px-4 rounded-3 btn-py-sm text-decoration-none"
                                >
                                  <i className="bi bi-eye me-1" />
                                  View
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
    </>
  );
}

export default Orders;
